/**
 * ViewRenderer.tsx - Universal view renderer
 * 
 * ARCHITECTURE:
 * Takes a view ID, looks it up in the registry, and renders the appropriate component.
 * Apps use this instead of importing specific view components.
 * 
 * USAGE:
 * <ViewRenderer viewId="contact-cards" data={contacts} />
 */

import React, { Suspense, lazy } from 'react';
import type { ViewrView } from '../types';
import { viewRegistry } from '../registry';
import { ViewLoader } from './ViewLoader';
import { ViewError } from './ViewError';

// Import lightweight views directly
import { KanbanViewOpCentral } from '../KanbanView';
import { DataTable } from '../DataTable';
import { GalleryView } from '../GalleryView';
import { TimelineView } from '../TimelineView';
import { DashboardView } from '../DashboardView';
import { NotesView } from '../NotesView';
import { FormView } from '../FormView';
import { PreviewView } from '../PreviewView';
// Temporarily disabled - fullcalendar dependency issues
// import { CalendarView } from '../CalendarView';
import { ContactCardView } from '../ContactCardView';
import { ContactListView } from '../ContactListView';
import { Chart } from '../Chart';
import { Outliner } from '../views/OutlineView';

// Lazy load heavy views
const Spreadsheet = lazy(() => import('../Spreadsheet/Spreadsheet').then(m => ({ default: m.Spreadsheet })));
const MindMapView = lazy(() => import('../MindMapView'));
const MapView = lazy(() => import('../MapView'));
const GanttView = lazy(() => import('../GanttView'));

/**
 * ViewRenderer props
 */
export interface ViewRendererProps {
  /** View ID to render */
  viewId: string;
  
  /** Data to pass to the view */
  data?: any;
  
  /** Callback for view actions */
  onAction?: (action: string, data: any) => void;
  
  /** Override view configuration */
  config?: Partial<any>;
  
  /** Custom class name */
  className?: string;
  
  /** Custom styles */
  style?: React.CSSProperties;
}

/**
 * Universal view renderer component
 */
export const ViewRenderer: React.FC<ViewRendererProps> = ({
  viewId,
  data,
  onAction,
  config,
  className,
  style,
}) => {
  // Look up view in registry
  const view = viewRegistry.get(viewId);

  // Handle view not found
  if (!view) {
    return (
      <ViewError
        error={`View not found: ${viewId}`}
        details="This view has not been registered. Check that the view definition is loaded."
      />
    );
  }

  // Merge config
  const mergedConfig = config ? { ...view.config, ...config } : view.config;

  // Render appropriate component based on view type
  try {
    return (
      <div className={className} style={style} data-view-id={viewId} data-view-type={view.type}>
        {renderViewComponent(view, data, mergedConfig, onAction)}
      </div>
    );
  } catch (error) {
    return (
      <ViewError
        error={`Error rendering view: ${viewId}`}
        details={error instanceof Error ? error.message : String(error)}
      />
    );
  }
};

/**
 * Render the appropriate component for a view type
 */
function renderViewComponent(
  view: ViewrView,
  data: any,
  config: any,
  onAction?: (action: string, data: any) => void
): React.ReactNode {
  const commonProps = {
    data,
    config,
    onAction,
  };

  switch (view.type) {
    // List-based views
    case 'list':
    case 'table':
      return <DataTable {...commonProps} columns={config.columns} />;

    case 'cards':
      return <ContactCardView {...commonProps} />;

    case 'spreadsheet':
      return (
        <Suspense fallback={<ViewLoader message="Loading spreadsheet..." />}>
          <Spreadsheet {...commonProps} />
        </Suspense>
      );

    // Organization views
    case 'kanban':
      return <KanbanViewOpCentral {...commonProps} />;

    case 'timeline':
      return <TimelineView {...commonProps} />;

    // Temporarily disabled - fullcalendar dependency issues
    // case 'calendar':
    //   return <CalendarView {...commonProps} />;

    case 'gantt':
      return (
        <Suspense fallback={<ViewLoader message="Loading Gantt chart..." />}>
          <GanttView {...commonProps} />
        </Suspense>
      );

    // Graph/Network views
    case 'graph':
    case 'network':
      return (
        <div className="graph-view-placeholder">
          <p>Graph view coming soon</p>
          <p>View ID: {view.id}</p>
        </div>
      );

    case 'mindmap':
      return (
        <Suspense fallback={<ViewLoader message="Loading mind map..." />}>
          <MindMapView {...commonProps} />
        </Suspense>
      );

    case 'tree':
    case 'outliner':
      return <Outliner {...commonProps} />;

    // Dashboard/Analytics views
    case 'dashboard':
      return <DashboardView {...commonProps} />;

    case 'chart':
      return <Chart {...commonProps} />;

    case 'metrics':
      return (
        <div className="metrics-view-placeholder">
          <p>Metrics view coming soon</p>
          <p>View ID: {view.id}</p>
        </div>
      );

    // Content views
    case 'gallery':
      return <GalleryView {...commonProps} />;

    case 'form':
      return <FormView {...commonProps} />;

    case 'detail':
      return (
        <div className="detail-view-placeholder">
          <p>Detail view coming soon</p>
          <p>View ID: {view.id}</p>
        </div>
      );

    case 'preview':
      return <PreviewView {...commonProps} />;

    case 'notes':
      return <NotesView {...commonProps} />;

    // Map views
    case 'map':
    case 'geospatial':
      return (
        <Suspense fallback={<ViewLoader message="Loading map..." />}>
          <MapView {...commonProps} />
        </Suspense>
      );

    // Fallback
    default:
      return (
        <ViewError
          error={`Unsupported view type: ${view.type}`}
          details={`View ID: ${view.id}. This view type is not yet implemented.`}
        />
      );
  }
}

/**
 * Hook to get a view from registry
 */
export function useView(viewId: string): ViewrView | undefined {
  return viewRegistry.get(viewId);
}

/**
 * Hook to check if view exists
 */
export function useViewExists(viewId: string): boolean {
  return viewRegistry.has(viewId);
}
